import { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { UserPlus, UserMinus, Users, Search, ArrowLeftRight, Merge, X } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EventPhotographerAssignment({ eventId, open, onOpenChange, onAssignmentChange }) {
  const [allPhotographers, setAllPhotographers] = useState([]);
  const [assignedPhotographers, setAssignedPhotographers] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [gearFilter, setGearFilter] = useState('all');
  
  // Swap states
  const [swapMode, setSwapMode] = useState(false);
  const [swapPhotographer1, setSwapPhotographer1] = useState(null);
  const [swapPhotographer2, setSwapPhotographer2] = useState(null);
  
  // Merge states
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeFrom, setMergeFrom] = useState(null);
  const [mergeTo, setMergeTo] = useState(null);

  const token = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    if (open) {
      fetchData();
      // Reset states when dialog opens
      setSearchTerm('');
      setGearFilter('all');
      setSwapMode(false);
      setMergeMode(false);
      setSwapPhotographer1(null);
      setSwapPhotographer2(null);
      setMergeFrom(null);
      setMergeTo(null);
    }
  }, [open, eventId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [allRes, assignedRes] = await Promise.all([
        axios.get(`${API}/photographers`, config),
        axios.get(`${API}/events/${eventId}/photographers`, config)
      ]);
      
      setAllPhotographers(allRes.data);
      setAssignedPhotographers(assignedRes.data);
    } catch (error) {
      toast.error('Failed to load photographers');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (photographerId) => {
    try {
      await axios.post(
        `${API}/events/${eventId}/assign-photographer/${photographerId}`,
        {},
        config
      );
      
      toast.success('Photographer assigned to event ✓', { duration: 2000 });
      fetchData();
      if (onAssignmentChange) onAssignmentChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign photographer');
    }
  };

  const handleUnassign = async (photographerId) => {
    if (!window.confirm('Are you sure you want to unassign this photographer? They will be removed from all spots.')) {
      return;
    }
    
    try {
      await axios.delete(
        `${API}/events/${eventId}/unassign-photographer/${photographerId}`,
        config
      );
      
      toast.success('Photographer unassigned from event ✓', { duration: 2000 });
      fetchData();
      if (onAssignmentChange) onAssignmentChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unassign photographer');
    }
  };

  const handleSwap = async () => {
    if (!swapPhotographer1 || !swapPhotographer2) {
      toast.error('Please select two photographers to swap');
      return;
    }
    
    if (swapPhotographer1.id === swapPhotographer2.id) {
      toast.error('Please select two different photographers');
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/events/${eventId}/swap-photographers`,
        null,
        {
          params: {
            photographer_id_1: swapPhotographer1.id,
            photographer_id_2: swapPhotographer2.id
          },
          ...config
        }
      );
      
      toast.success(`Swapped assignments: ${response.data.spots_affected} spots affected ✓`, { duration: 3000 });
      setSwapMode(false);
      setSwapPhotographer1(null);
      setSwapPhotographer2(null);
      fetchData();
      if (onAssignmentChange) onAssignmentChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to swap photographers');
    }
  };

  const handleMerge = async () => {
    if (!mergeFrom || !mergeTo) {
      toast.error('Please select both photographers for merge');
      return;
    }
    
    if (mergeFrom.id === mergeTo.id) {
      toast.error('Please select two different photographers');
      return;
    }
    
    if (!window.confirm(`Merge all spots from ${mergeFrom.name} to ${mergeTo.name}? ${mergeFrom.name} will be removed from all spots.`)) {
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/events/${eventId}/merge-photographer-spots`,
        null,
        {
          params: {
            from_photographer_id: mergeFrom.id,
            to_photographer_id: mergeTo.id
          },
          ...config
        }
      );
      
      toast.success(`Merged ${response.data.spots_merged} spots from ${mergeFrom.name} to ${mergeTo.name} ✓`, { duration: 3000 });
      setMergeMode(false);
      setMergeFrom(null);
      setMergeTo(null);
      fetchData();
      if (onAssignmentChange) onAssignmentChange();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to merge photographers');
    }
  };

  const assignedIds = assignedPhotographers.map(p => p.id);
  
  // Get all unique gear types
  const allGearTypes = [...new Set(
    allPhotographers.flatMap(p => 
      (p.personal_gear || []).map(g => g.name)
    )
  )];

  // Filter photographers based on search and gear filter
  const filterPhotographers = (photographers) => {
    return photographers.filter(photographer => {
      // Search filter
      const matchesSearch = !searchTerm || 
        photographer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        photographer.email.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Gear filter
      const matchesGear = gearFilter === 'all' || 
        (photographer.personal_gear || []).some(g => g.name === gearFilter);
      
      return matchesSearch && matchesGear;
    });
  };

  const filteredAssigned = filterPhotographers(assignedPhotographers);
  const filteredAvailable = filterPhotographers(
    allPhotographers.filter(photographer => !assignedIds.includes(photographer.id))
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Assign Photographers to Event</span>
            <div className="flex gap-2">
              {!swapMode && !mergeMode && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSwapMode(true);
                      setMergeMode(false);
                    }}
                    className="text-xs"
                  >
                    <ArrowLeftRight className="h-3 w-3 mr-1" />
                    Swap
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setMergeMode(true);
                      setSwapMode(false);
                    }}
                    className="text-xs"
                  >
                    <Merge className="h-3 w-3 mr-1" />
                    Merge
                  </Button>
                </>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : (
          <div className="space-y-4">
            {/* Swap Mode */}
            {swapMode && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                    <ArrowLeftRight className="h-4 w-4" />
                    Swap Photographers
                  </h3>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setSwapMode(false);
                      setSwapPhotographer1(null);
                      setSwapPhotographer2(null);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm text-blue-700">Swap all spot assignments between two photographers</p>
                <div className="grid grid-cols-2 gap-3">
                  <Select
                    value={swapPhotographer1?.id || ''}
                    onValueChange={(value) => {
                      const photographer = assignedPhotographers.find(p => p.id === value);
                      setSwapPhotographer1(photographer);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select first photographer" />
                    </SelectTrigger>
                    <SelectContent>
                      {assignedPhotographers.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select
                    value={swapPhotographer2?.id || ''}
                    onValueChange={(value) => {
                      const photographer = assignedPhotographers.find(p => p.id === value);
                      setSwapPhotographer2(photographer);
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select second photographer" />
                    </SelectTrigger>
                    <SelectContent>
                      {assignedPhotographers.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleSwap}
                  disabled={!swapPhotographer1 || !swapPhotographer2}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  Swap Assignments
                </Button>
              </div>
            )}

            {/* Merge Mode */}
            {mergeMode && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-purple-900 flex items-center gap-2">
                    <Merge className="h-4 w-4" />
                    Merge Photographer Spots
                  </h3>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setMergeMode(false);
                      setMergeFrom(null);
                      setMergeTo(null);
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-sm text-purple-700">Transfer all spots from one photographer to another</p>
                <div className="space-y-2">
                  <div>
                    <label className="text-xs text-purple-700 font-medium">From (will lose spots):</label>
                    <Select
                      value={mergeFrom?.id || ''}
                      onValueChange={(value) => {
                        const photographer = assignedPhotographers.find(p => p.id === value);
                        setMergeFrom(photographer);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select source photographer" />
                      </SelectTrigger>
                      <SelectContent>
                        {assignedPhotographers.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-xs text-purple-700 font-medium">To (will receive spots):</label>
                    <Select
                      value={mergeTo?.id || ''}
                      onValueChange={(value) => {
                        const photographer = assignedPhotographers.find(p => p.id === value);
                        setMergeTo(photographer);
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select target photographer" />
                      </SelectTrigger>
                      <SelectContent>
                        {assignedPhotographers.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button
                  onClick={handleMerge}
                  disabled={!mergeFrom || !mergeTo}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  Merge All Spots
                </Button>
              </div>
            )}

            {/* Search and Filter */}
            {!swapMode && !mergeMode && (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search photographers..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  
                  {/* Gear Filter */}
                  <Select value={gearFilter} onValueChange={setGearFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by gear" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Gear</SelectItem>
                      {allGearTypes.map(gear => (
                        <SelectItem key={gear} value={gear}>{gear}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Assigned Photographers */}
            {!swapMode && !mergeMode && (
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Assigned ({filteredAssigned.length})
                </h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {filteredAssigned.length === 0 ? (
                    <p className="text-gray-400 text-sm py-4 text-center">
                      {searchTerm || gearFilter !== 'all' 
                        ? 'No photographers match your filters'
                        : 'No photographers assigned yet'}
                    </p>
                  ) : (
                    filteredAssigned.map(photographer => (
                      <div
                        key={photographer.id}
                        className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg"
                      >
                        <div className="flex-1">
                          <div className="font-medium">{photographer.name}</div>
                          <div className="text-sm text-gray-600">{photographer.email}</div>
                          {photographer.personal_gear && photographer.personal_gear.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {photographer.personal_gear.slice(0, 3).map((gear, idx) => (
                                <span key={idx} className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">
                                  {gear.name}
                                </span>
                              ))}
                              {photographer.personal_gear.length > 3 && (
                                <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded">
                                  +{photographer.personal_gear.length - 3}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleUnassign(photographer.id)}
                        >
                          <UserMinus className="h-4 w-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Available Photographers */}
            {!swapMode && !mergeMode && (
              <div>
                <h3 className="font-semibold mb-2">Available Photographers ({filteredAvailable.length})</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {filteredAvailable.length === 0 ? (
                    <p className="text-gray-400 text-sm py-4 text-center">
                      {searchTerm || gearFilter !== 'all'
                        ? 'No photographers match your filters'
                        : 'All photographers are already assigned to this event'}
                    </p>
                  ) : (
                    filteredAvailable.map(photographer => (
                      <div
                        key={photographer.id}
                        className="flex items-center justify-between p-3 bg-gray-50 border rounded-lg"
                      >
                        <div className="flex-1">
                          <div className="font-medium">{photographer.name}</div>
                          <div className="text-sm text-gray-600">{photographer.email}</div>
                          {photographer.personal_gear && photographer.personal_gear.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {photographer.personal_gear.slice(0, 3).map((gear, idx) => (
                                <span key={idx} className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                                  {gear.name}
                                </span>
                              ))}
                              {photographer.personal_gear.length > 3 && (
                                <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-700 rounded">
                                  +{photographer.personal_gear.length - 3}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAssign(photographer.id)}
                        >
                          <UserPlus className="h-4 w-4" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            <div className="flex justify-end pt-2 border-t">
              <Button onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
